import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase/client';
import DemoRequestModal from '@/components/feature/DemoRequestModal';

const NETWORK_ERROR_MESSAGE =
  'No pudimos conectar con FoodDash. Verifica tu internet y vuelve a intentarlo.';

function getAuthErrorMessage(cause: unknown, fallback: string) {
  const message = cause instanceof Error ? cause.message : '';
  if (/failed to fetch|fetch failed|networkerror|network request failed/i.test(message)) {
    return NETWORK_ERROR_MESSAGE;
  }
  return message || fallback;
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [recoverySent, setRecoverySent] = useState(false);
  const [showDemoRequest, setShowDemoRequest] = useState(false);

  const handlePasswordRecovery = async () => {
    if (!email) {
      setError('Ingresa primero tu correo electrónico.');
      return;
    }
    setLoading(true);
    setError('');
    setRecoverySent(false);
    try {
      const { error: recoveryError } = await supabase.auth.resetPasswordForEmail(
        email.trim().toLowerCase(),
        { redirectTo: `${window.location.origin}/reset-password` },
      );
      if (recoveryError) throw recoveryError;
      setRecoverySent(true);
    } catch (cause) {
      setError(getAuthErrorMessage(cause, 'No fue posible enviar el correo de recuperación.'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Ingresa tu correo y contraseña');
      return;
    }
    setError('');
    setLoading(true);

    try {
      const user = await login(email, password);
      if (user.role === 'employee') {
        navigate('/orders/capture');
      } else {
        navigate('/dashboard');
      }
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : 'No fue posible iniciar sesión.';
      setError(getAuthErrorMessage(cause, message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-50 flex flex-col min-[700px]:flex-row">
      <div className="hidden min-[700px]:flex min-[700px]:w-[46%] xl:w-[52%] relative overflow-hidden bg-[#461506]">
        <picture className="absolute inset-0 block h-full w-full">
          <source
            media="(max-aspect-ratio: 17/10)"
            srcSet="/brand/fooddash-login-tablet.jpg"
          />
          <img
            src="/brand/fooddash-login-desktop.jpg"
            alt="FoodDash: gestiona pedidos y operaciones de tu negocio de comida"
            className="h-full w-full object-cover object-center"
            loading="eager"
            fetchPriority="high"
          />
        </picture>
        <span className="sr-only">500 negocios, 50 mil pedidos al día y 99.9% de disponibilidad.</span>
      </div>

      <div className="flex-1 flex items-center justify-center p-5 min-[700px]:p-7 xl:p-12 overflow-y-auto">
        <div className="w-full max-w-[440px] animate-scale-in">
          <div className="min-[700px]:hidden text-center mb-8">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3 overflow-hidden">
              <img src="/brand/fooddash-mark-orange.png" alt="FoodDash" className="w-full h-full object-contain" />
            </div>
            <h1 className="text-2xl font-bold font-heading"><span className="text-foreground-950">Food</span><span className="text-primary-600">Dash</span></h1>
          </div>

          <div className="mb-6">
            <h2 className="text-2xl font-bold text-foreground-900 font-heading mb-1">Iniciar Sesión</h2>
            <p className="text-sm text-foreground-600">Accede a tu panel de gestión</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-foreground-700 mb-1.5">
                Correo electrónico
              </label>
              <div className="relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@negocio.com"
                  className="w-full h-[50px] pl-11 pr-4 rounded-xl border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 text-sm font-medium focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                />
              </div>
              <div className="mt-2 text-right">
                <button
                  type="button"
                  onClick={handlePasswordRecovery}
                  disabled={loading}
                  className="text-sm font-semibold text-primary-600 hover:text-primary-700 disabled:opacity-50"
                >
                  Olvidé mi contraseña
                </button>
              </div>
            </div>

            {recoverySent && (
              <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                <i className="ri-checkbox-circle-line"></i>
                Revisa tu correo. Te enviamos un enlace para crear una nueva contraseña.
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-foreground-700 mb-1.5">
                Contraseña
              </label>
              <div className="relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400">
                  <i className="ri-lock-line text-lg"></i>
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full h-[50px] pl-11 pr-4 rounded-xl border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 text-sm font-medium focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                />
              </div>
            </div>

            {error && (
              <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-center gap-2">
                <i className="ri-error-warning-line"></i>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full h-[50px] bg-primary-600 hover:bg-primary-700 shadow-sm shadow-primary-900/15 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-xl text-base font-semibold transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Ingresando...
                </>
              ) : (
                <>
                  Ingresar
                  <i className="ri-arrow-right-line"></i>
                </>
              )}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-foreground-500">
            ¿No tienes cuenta?{' '}
            <button type="button" onClick={() => setShowDemoRequest(true)} className="font-semibold text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap">
              Solicitar demo
            </button>
          </p>
          <button type="button" onClick={() => navigate('/demo')} className="mt-3 w-full text-sm font-semibold text-foreground-600 hover:text-primary-600">
            <i className="ri-play-circle-line mr-1" /> Ver demo interactiva
          </button>
        </div>
      </div>
      {showDemoRequest && <DemoRequestModal source="login" onClose={() => setShowDemoRequest(false)} />}
    </div>
  );
}
